import usePageTitle from '../hooks/usePageTitle';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, Trash2, ShoppingCart, Package } from 'lucide-react';
import { getImageUrl } from '../lib/api';
import { toast } from 'sonner';

const WishlistPage = () => {
  usePageTitle('Wishlist');
  const { wishlistItems, loading, fetchWishlist, removeFromWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [isAuthenticated, fetchWishlist]);

  if (!isAuthenticated) {
    return (
      <div className="container-wide py-16 text-center">
        <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#0F172A] mb-2">My Wishlist</h1>
        <p className="text-[#64748B] mb-6">Please login to view your wishlist</p>
        <Link to="/login">
          <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
            Login
          </Button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-wide py-8">
        <h1 className="text-3xl font-bold text-[#0F172A] mb-8">My Wishlist</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[3/4] bg-gray-200 mb-4" />
              <div className="h-4 bg-gray-200 w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in" data-testid="wishlist-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A]">My Wishlist</h1>
          <p className="text-[#64748B] mt-1">
            {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>
      </div>

      {wishlistItems.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Your wishlist is empty</h2>
          <p className="text-[#64748B] mb-8">Save items you love by clicking the heart icon</p>
          <Link to="/products">
            <Button className="bg-[#002FA7] hover:bg-[#0025a0]">
              Browse Products
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {wishlistItems.map((product) => (
            <WishlistItem 
              key={product.id} 
              product={product} 
              onRemove={() => removeFromWishlist(product.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WishlistItem = ({ product, onRemove }) => {
  const imageUrl = getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';
  const totalStock = product.inventory?.reduce((acc, inv) => acc + inv.quantity, 0) || 0;
  const isOutOfStock = totalStock === 0;
  const isOnSale = product.is_on_sale;

  return (
    <div className="group relative" data-testid={`wishlist-item-${product.id}`}>
      {/* Remove Button */}
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 z-10 p-2 bg-white/90 rounded-full text-gray-500 hover:text-red-500 hover:bg-white transition-colors"
        aria-label="Remove from wishlist"
      >
        <Trash2 className="w-4 h-4" />
      </button>

      {/* Product Link */}
      <Link to={`/products/${product.id}`} className="block">
        <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4 relative">
          <img
            src={imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          
          {/* Out of Stock Overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Badge className="bg-red-500 text-white">Out of Stock</Badge>
            </div>
          )}

          {/* Sale Badge */}
          {isOnSale && !isOutOfStock && (
            <Badge className="absolute top-3 left-3 bg-red-500 text-white">SALE</Badge>
          )}
        </div>

        <h3 className="font-medium text-[#0F172A] mb-1 line-clamp-2 group-hover:underline">
          {product.name}
        </h3>
        
        <p className="text-sm text-[#64748B] mb-2">{product.sku}</p>
        
        <div className="flex items-center gap-2">
          {isOnSale ? (
            <>
              <span className="font-bold text-red-600">${product.sale_price?.toFixed(2)}</span>
              <span className="text-sm text-gray-400 line-through">${product.price?.toFixed(2)}</span>
            </>
          ) : (
            <span className="font-bold text-[#0F172A]">${product.price?.toFixed(2)}</span>
          )}
        </div>

        {!isOutOfStock && (
          <p className="text-xs text-green-600 mt-1">{totalStock} in stock</p>
        )}
      </Link>

      {/* Quick Add Button */}
      {!isOutOfStock && (
        <Link to={`/products/${product.id}`} className="mt-3 block">
          <Button 
            variant="outline" 
            className="w-full text-sm"
            size="sm"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            View & Add to Cart
          </Button>
        </Link>
      )}
    </div>
  );
};

export default WishlistPage;
